<template>
  <div class="weight-balance-container">
    <div class="grid-form">
      <div class="grid-form-col header-cols">
        <div v-for="(header, index) in headers" :key="index">
          {{ header }}
        </div>
      </div>

      <div v-for="(row, rowIndex) in rows" :key="rowIndex" class="grid-form-col sm">
        <div>{{ row.label }}</div>
        <input :disabled="loading" v-model="form[row.weight]" type="text" placeholder="+ Weight...">
        <div class="relative">
          <input :disabled="loading" v-if="row.arm" v-model="form[row.arm]" type="text" placeholder="Arm...">
          <div class="custom-placeholder" v-if="row.suffix">CG</div>
        </div>
        <input :disabled="loading" v-if="row.moment" v-model="form[row.moment]" type="text" placeholder="+ Moment...">
      </div>
    </div>

    <div>
      <button class="primary" @click="handleNext" :disabled="loading">
        <b-spinner
          v-if="loading"
          label="Loading..."
          variant="light"
          class="spinner-sm"
        ></b-spinner>
        Next
      </button>
    </div>
  </div>
</template>

<script>
import DispatchAPIService from "@/servicehandlers/DispatchAPIService";
import AircraftAPIService from "../../servicehandlers/AircraftAPIService.js";

const dispatchAPIService = new DispatchAPIService();
const aircraftAPIService = new AircraftAPIService();

export default {
  props: {
    toast: {
      type: Object,
      required: true,
      default: () => {}
    },
    dispatchId: {
      type: Number
    },
    flightDetails: {
      type: Object
    }
  },
  data() {
    return {
      loading: false,
      aircraft: null,
      form: {
        emptyWeightWeight: 0.0,
        emptyWeightArm: 0.0,
        emptyWeightMoment: 0.0,
        frontPilotsWeight: 340,
        frontPilotsArm: 80.5,
        frontPilotsMoment: 27370,
        passengersWeight: 200,
        passengersArm: 118.1,
        passengersMoment: 23620,
        baggageWeight: 50,
        baggageArm: 142.8,
        baggageMoment: 7140,
        useableFuelWeight: 300,
        useableFuelArm: 95,
        useableFuelMoment: 28500,
        taxiFuelWeight: 10,
        taxiFuelArm: 95,
        taxiFuelMoment: 950,
        fuelBurnWeight: 200,
        fuelBurnArm: 95,
        fuelBurnMoment: 19000
      },
      headers: ["", "Weight", "Arm", "Moment"],
      rows: [
        { label: "Empty Weight", weight: "emptyWeightWeight", arm: "emptyWeightArm", moment: "emptyWeightMoment" },
        { label: "Front Pilots", weight: "frontPilotsWeight", arm: "frontPilotsArm", moment: "frontPilotsMoment" },
        { label: "Passengers", weight: "passengersWeight", arm: "passengersArm", moment: "passengersMoment" },
        { label: "Baggage", weight: "baggageWeight", arm: "baggageArm", moment: "baggageMoment" },
        { label: "Zero Fuel Wt.", weight: "zeroFuelWeight", arm: "zeroFuelArm", moment: "zeroFuelMoment", suffix: true },
        { label: "Usable Fuel", weight: "useableFuelWeight", arm: "useableFuelArm", moment: "useableFuelMoment" },
        { label: "Ramp Weight", weight: "rampWeightWeight", arm: "rampWeightArm", moment: "rampWeightMoment" },
        { label: "Taxi Fuel", weight: "taxiFuelWeight", arm: "taxiFuelArm", moment: "taxiFuelMoment" },
        { label: "Takeoff Weight", weight: "takeoffWeightWeight", arm: "takeoffWeightArm", moment: "takeoffWeightMoment", suffix: true },
        { label: "Fuel Burn", weight: "fuelBurnWeight", arm: "fuelBurnArm", moment: "fuelBurnMoment" },
        { label: "Landing Weight", weight: "landingWeightWeight", arm: "landingWeightArm", moment: "landingWeightMoment", suffix: true }
      ]
    };
  },
  async mounted() {
    await this.getAircraft();
  },
  methods: {
    async getAircraft() {
      try {
        this.aircraft = await aircraftAPIService.getById(this.flightDetails.aircraftId, this.$router)
        this.form.emptyWeightWeight = this.aircraft.emptyWeight
        this.form.emptyWeightMoment = this.aircraft.moment

        // TODO Disable data fields that import from aircraft db

        // TODO Aircraft schema will need to be updated before auto calculated fields can be coded
          // Missing: ARM, max forward arm, max aft arm

        // Fields we already have that may or may not be needed on this page (I'm not sure):
        // aircraft.fuelCapacity?
        // aircraft.usefulLoad?
        // aircraft.maxGrossWeight

      } catch (error) {
        this.toast.showError('Unable to get aircrafts');
      }
    },
    async handleNext() {
      try {
        this.loading = true;

        let totalWeight = 0;
        let totalMoment = 0;
        let takeoffWeight = 0;
        let takeoffMoment = 0;
        let fuelBurnWeight = 0;
        let fuelBurnMoment = 0;
        let landingWeight = 0;
        let landingMoment = 0;

        this.rows.forEach(row => {
          const weight = parseFloat(this.form[row.weight]) || 0;
          const arm = parseFloat(this.form[row.arm]) || 0;
          const moment = weight * arm;

          totalWeight += weight;
          totalMoment += moment;

          if (row.label === "Fuel Burn") {
            fuelBurnWeight = weight;
            fuelBurnMoment = fuelBurnWeight * (parseFloat(this.form.fuelBurnArm) || 0);
          } else {
            takeoffWeight = totalWeight;
            takeoffMoment = totalMoment;
          }
        });

        landingWeight = takeoffWeight - fuelBurnWeight;
        landingMoment = takeoffMoment - fuelBurnMoment;

        const takeoffCG = this.calculateCG(takeoffWeight, takeoffMoment);
        const landingCG = this.calculateCG(landingWeight, landingMoment);

        // create weight balance
        const weightBalance = await dispatchAPIService.createWeightBalance(this.flightDetails.id, this.form)

        // this.form.dispatchId = this.dispatchId;
        // await dispatchAPIService.createWeightBalance(this.form);

        this.$emit('next', {
          tw: { x: parseFloat(takeoffCG), y: takeoffWeight },
          lw: { x: parseFloat(landingCG), y: landingWeight },
          weightBalance: weightBalance
        });
      } catch (error) {
        this.toast.showError('Unable to proceed');
      } finally {
        this.loading = false;
      }
    },
    calculateCG(weight, moment) {
      return weight > 0 ? (moment / weight).toFixed(2) : 0;
    }
  }
};
</script>

<style>
.weight-balance-container {
  max-width: 650px;
}
.grid-form {
  display: flex;
  flex-direction: column;
}
.grid-form-col {
  display: grid;
  grid-template-columns: .8fr 1fr 1fr 1fr;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.header-cols div {
  font-weight: bold;
  color: #404040;
}
.custom-placeholder {
  position: absolute;
  right: 10px;
  top: 12px;
  color: #9a9a9a;
}
.relative {
  position: relative;
}
</style>
